/**
 * Helpers for legacy Sunrise CRM tickets, which are rendered by the in-app
 * ticket page instead of linking to the (rarely installed) Sunrise client.
 */

export const SUNRISE_SOURCE = 'sunrise';

const DOCUMENT_ID_PREFIX = 'sunrise:ticket:';
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Must match `_synthesize_sunrise_body` in import/extractors/sunrise_tickets.py.
const SOLUTION_MARKER = /^-{5} Solution -{5}[ \t]*$/m;

/** In-app path of a ticket, or `null` if `documentId` is not a Sunrise ticket ID. */
export function sunriseTicketPath(documentId: string | undefined): string | null {
  if (!documentId?.startsWith(DOCUMENT_ID_PREFIX)) return null;
  const ticketId = documentId.slice(DOCUMENT_ID_PREFIX.length);
  return GUID_PATTERN.test(ticketId) ? `/sunrise/tickets/${ticketId}` : null;
}

/** Search document ID for a ticket GUID from the URL, or `null` if malformed. */
export function sunriseDocumentId(ticketId: string | undefined): string | null {
  return ticketId && GUID_PATTERN.test(ticketId) ? `${DOCUMENT_ID_PREFIX}${ticketId}` : null;
}

export interface SunriseTicketSections {
  description: string;
  solution: string;
}

/** Split the synthesized ticket markdown into its description and solution parts. */
export function splitSunriseContent(markdown: string | undefined): SunriseTicketSections {
  // The importer prepends the title as an H1, which the page already shows.
  const body = (markdown ?? '').replace(/^#[ \t]+[^\n]*(\n|$)/, '');
  const match = SOLUTION_MARKER.exec(body);
  if (!match) return { description: body.trim(), solution: '' };
  return {
    description: body.slice(0, match.index).trim(),
    solution: body.slice(match.index + match[0].length).trim(),
  };
}
